/**
 * The data colours of the team reports, the KPI pages and the shot charts: red = bad, green = good, everywhere. The
 * staff's picks of 2026-10-10 (coaches and GM), chosen in a local switcher that has since been removed: the rebounding
 * page's red and green, in seven buckets with a white middle.
 */
import { alpha } from "@mui/material/styles";

/** The rebounding board's red and green (Rebounding/reboundingModel.ts PAL.White), without its amber middle. */
export const REPORT_SCALE = { bad: "#F0261B", good: "#1F9D55" };

/** A bucket's strength toward its end of the scale: 3 = most (the full colour), 0 = white, negative = the bad side. */
export type Step = -3 | -2 | -1 | 0 | 1 | 2 | 3;

// Each bucket's last rank of 30, counted from the best end: ranks 1-5, 6-10, 11-14, 15-16 (white), 17-20, 21-25,
// 26-30. Other populations use the same shares.
const BUCKETS: [number, Step][] = [
  [5, 3],
  [10, 2],
  [14, 1],
  [16, 0],
  [20, -1],
  [25, -2],
  [30, -3],
];

// Opacity over white per step. 0.65 / 0.30 keep each step 13-22 ΔE2000 from its neighbours, so the lightest tint
// still reads as coloured in print.
const STEP_ALPHA: Record<0 | 1 | 2 | 3, number> = {
  0: 0,
  1: 0.3,
  2: 0.65,
  3: 1,
};

/** The buckets from worst to best, as shares of 30 (for the legend, drawn bad-left, good-right). */
export function bucketSpans(): { from: number; to: number; step: Step }[] {
  let prev = 0;
  return BUCKETS.map(([last, step]) => {
    const span = { from: 30 - last, to: 30 - prev, step };
    prev = last;
    return span;
  }).reverse();
}

/** The bucket of a rank (1 = best) out of `population`. */
export function stepFromRank(rank: number, population: number = 30): Step {
  // Cross-multiplying keeps the edges exact: rank 5 of 30 is in the top bucket, rank 6 is not
  const hit = BUCKETS.find(([last]) => rank * 30 <= last * population);
  return (hit ?? BUCKETS[BUCKETS.length - 1])[1];
}

/** The bucket of a 0-100 percentile (100 = best). */
export function stepFromPercentile(percentile: number): Step {
  // The share of the population above it, in 30ths. A bucket keeps its lower percentile edge, as rank 14 of 30
  // (p53.3) sits in 11-14; the tolerance absorbs float noise from rank → percentile conversions.
  const above = ((100 - percentile) * 30) / 100;
  const hit = BUCKETS.find(([last]) => above <= last + 1e-9);
  return (hit ?? BUCKETS[BUCKETS.length - 1])[1];
}

const strength = (step: Step) => STEP_ALPHA[Math.abs(step) as 0 | 1 | 2 | 3];
const sideOf = (step: Step) =>
  step >= 0 ? REPORT_SCALE.good : REPORT_SCALE.bad;

/** A step's colour as the chips render it: translucent, so zebra rows show through the white middle. */
export function stepColor(step: Step): string {
  return alpha(sideOf(step), strength(step));
}

/** A step's colour flattened onto white. */
export function stepHexOverWhite(step: Step): string {
  return overWhite(sideOf(step), strength(step));
}

/** The ring around a white middle-bucket cell, so it doesn't vanish on the page. Inset: it takes no space. */
export const WHITE_CELL_OUTLINE = "inset 0 0 0 1px #D0D0D0";

/** KPI pages: the met block is the scale's green with dark ink (5.0:1; white would be 3.5:1). Missed stays white. */
export const KPI_MET = { bg: REPORT_SCALE.good, fg: "#1a1a1a" };

/** Shot charts: filled circle = make, cross = miss (shape as well as colour, so they survive greyscale printing). */
export const SHOT_MARKS = { make: "#222", miss: "#9E9E9E" };

/** Hexbin shot charts colour a spot by its absolute TS% in this scale (Trevor 2026-10-27): seven buckets 5 points wide,
 * the middle around league-average TS% (55-60%). Always the shooter's view: high TS% is green on a defense chart
 * too (a defense flip was tried on the old ramp and reverted). Upper edges, in TS% points, worst first. */
export const TS_STEP_EDGES = [45, 50, 55, 60, 65, 70];

/** The bucket of a TS% given as a share (0.57). */
export function stepFromTs(ts: number): Step {
  const i = TS_STEP_EDGES.findIndex((edge) => ts * 100 < edge);
  return ((i < 0 ? TS_STEP_EDGES.length : i) - 3) as Step;
}

/** The hexbins' middle bucket: a light warm grey, not the tables' white. A white hex on the white court vanished
 * (Trevor 2026-10-27), and the grey sits between the light red and light green tints in lightness. */
export const TS_MIDDLE_FILL = "#D7D4CF";

const hexStepFill = (step: Step) => (step === 0 ? TS_MIDDLE_FILL : stepHexOverWhite(step));

/** A hexbin's fill: its TS% bucket flattened onto white (the court is white), the grey middle; with no TS%, the
 * court's hairline grey. */
export function tsHexFill(ts: number | null | undefined): string {
  return ts == null ? "#e6e4e1" : hexStepFill(stepFromTs(ts));
}

/** The seven hexbin fills, worst to best (legends, and charts that bucket their own value). */
export const TS_STEP_FILLS = ([-3, -2, -1, 0, 1, 2, 3] as Step[]).map(hexStepFill);

export function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace("#", "");
  return [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16)) as [
    number,
    number,
    number,
  ];
}

/** A colour at `opacity` over white, as an opaque hex. */
export function overWhite(hex: string, opacity: number): string {
  return `#${hexToRgb(hex)
    .map((c) => Math.round(255 - opacity * (255 - c)))
    .map((c) => c.toString(16).padStart(2, "0"))
    .join("")}`;
}
